import {
  Flex,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { getIn } from 'formik';
import lodash from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { covenantPostgresQuery as commonCovPgQuery } from '@finley-technologies/covenant-calculations';
import {
  AssetVerification,
  CreditAgreement,
  typeguards,
} from '@finley-technologies/finley-types';

import { updatePostgresQueryArrayResultShell } from './ReportingOutputDocxAddConfigModal';
import { ReportingOutputDocxColumnConfigContainer } from './ReportingOutputDocxColumnConfigContainer';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { inputWidth } from '~/packages/athena/ui/consts';
import {
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
} from '~/packages/common-frontend/components/IconButton';
import {
  formatSelectOptions,
  Select,
} from '~/packages/common-frontend/components/Select';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { DeleteConfirmationModal } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPostgresQueryModal } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/entity//covenantPostgresQuery/EditPostgresQuery';
import { CreditAgreementFullArtifactWipConfigType } from '~/packages/creditAgreements/ui/types';

type PostgresQueryType = CreditAgreement.Config.CommonPostgresQuery.Query.QueryArrayResultSet;

// compile-time type check to ensure all properties of AssetVerification.AssetVerificationOutput.ReportEntry
// are included in the column name array
// see https://github.com/microsoft/TypeScript/issues/53171#issuecomment-1462568052
type CheckForMissing<
  Arr extends readonly unknown[],
  _Keys extends Arr[number]
> = {};
type CheckForExcess<_Arr extends readonly Keys[], Keys> = {};
// used for type check, see above - exported to avoid unused variable error
export type _assetVerificationMissingTypeCheck = CheckForMissing<
  typeof assetVerificationColumns,
  keyof AssetVerification.AssetVerificationOutput.ReportEntry
>;
// used for type check, see above - exported to avoid unused variable error
export type _assetVerificationExcessTypeCheck = CheckForExcess<
  typeof assetVerificationColumns,
  keyof AssetVerification.AssetVerificationOutput.ReportEntry
>;

const assetVerificationColumns = [
  'externalReceivableId',
  'finleyReceivableId',
  'status',
  'reason',
  'uploadDate',
  'verificationDate',
  'field',
  'loanTapeValue',
  'documentValue',
] as const;

const queryResultSetTypeOptions = formatSelectOptions(
  CreditAgreement.Config.Reporting.PostgresQuery.QueryArrayResultTypeIterable,
  'title',
);

type ReportingOutputDocxFileInsertionPostgresQueryOutputProps = {
  fileInsertionOutputName: string;
  postgresQueryOutputConfig: CreditAgreement.Config.Reporting.Outputs.Docx.PostgresQueryFileInsertionConfig;
  existingFileInsertionOutputs: CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionOutput[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  basePropertyKey: string;
  idx: number;
};
export const ReportingOutputDocxFileInsertionPostgresQueryOutput = ({
  fileInsertionOutputName,
  postgresQueryOutputConfig,
  existingFileInsertionOutputs,
  wipConfig,
  fullPropertyKey,
  basePropertyKey,
  idx,
}: ReportingOutputDocxFileInsertionPostgresQueryOutputProps) => {
  const toast = useToast();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { postgresQuery } = postgresQueryOutputConfig;
  const queryPropertyKey = `${fullPropertyKey}.config.postgresQuery`;

  const columnNames = useMemo((): string[] => {
    switch (postgresQueryOutputConfig.postgresQuery.type) {
      case 'assetVerification': {
        return [...assetVerificationColumns];
      }
      default:
        return finleyAssertUnreachable(
          postgresQueryOutputConfig.postgresQuery.type,
        );
    }
  }, [postgresQueryOutputConfig.postgresQuery.type]);

  const deleteQuery = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const fileInsertionConfigs: CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionOutput[] = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );

    finleyAssert(
      typeguards.creditAgreement.isDocxFileInsertionOutputs(
        fileInsertionConfigs,
      ),
      `Retrieved non-DocxFileInsertionOutputs structure for [key:${basePropertyKey}]`,
    );

    const configsAfterDelete = fileInsertionConfigs.filter(
      (_unused, varIdx) => varIdx !== idx,
    );

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: configsAfterDelete,
      setIsSaveLoading: setIsDeleteLoading,
    });
  }, [wipConfig, basePropertyKey, idx]);

  const [
    isFileOutputNameEditOpen,
    setFileOutputNameEditOpen,
  ] = useState<boolean>(false);
  const [fileOutputName, setFileOutputName] = useState<string>(
    fileInsertionOutputName,
  );

  const changeFileOutputName = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const nameExists = existingFileInsertionOutputs.some(
      (output) => output.name === fileOutputName,
    );

    if (nameExists) {
      toast({
        ...errorToastConfig,
        title: 'Updating Output Variable Name Error',
        description: `"${fileOutputName}" name is already in use`,
      });
      return;
    }

    const fileOutput = lodash.cloneDeep(
      getIn(wipConfig.formProps.values, fullPropertyKey),
    );

    fileOutput.name = fileOutputName;
    wipConfig.formProps.setFieldValue(fullPropertyKey, fileOutput);
    setFileOutputNameEditOpen(false);
  }, [
    existingFileInsertionOutputs,
    fileOutputName,
    fullPropertyKey,
    setFileOutputNameEditOpen,
    wipConfig,
    toast,
  ]);

  const onSave = useCallback(
    async (query: PostgresQueryType) => {
      if (wipConfig?.isMutateAllowed(queryPropertyKey)) {
        const validateResult = commonCovPgQuery.validatePostgresQuery({
          postgresQuery: {
            ...query,
            variables: [...query.variables],
          },
        });
        if (validateResult.errors.length) {
          toast({
            title: JSON.stringify(validateResult.errors),
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }

        await wipConfig.saveAndUpdateConfigCreditAgreementWip({
          wipConfig,
          propertyKey: queryPropertyKey,
          updatedProperty: query,
        });
      }
      setIsOpen(false);
    },
    [wipConfig, queryPropertyKey, toast],
  );

  return (
    <Flex mb="2" p="3" flexDir="column" border="1px" borderColor="gray.700">
      <Flex alignItems="center" mb={isQueryOpen ? '2' : undefined}>
        <Text mr="2">Postgres Query: &quot;{fileOutputName}&quot;</Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <IconButton
              aria-label={`${isQueryOpen ? 'close' : 'display'} config`}
              icon={isQueryOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setIsQueryOpen((prev) => !prev)}
              variant="ghost"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>

      {isQueryOpen ? (
        <>
          {/* *** File Insertion Output Name Management *** */}
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="4"
          >
            <Text width="60">Output Variable Name:</Text>
            {isFileOutputNameEditOpen &&
            wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <>
                <Input
                  type="text"
                  width={inputWidth}
                  value={fileOutputName}
                  onChange={(e) => setFileOutputName(e.target.value)}
                />
                <SaveIconButton
                  ariaLabel="save name"
                  label="Save Name"
                  onClick={() => changeFileOutputName()}
                />
                <EditIconButton
                  ariaLabel="close edit"
                  label="Close Edit"
                  onClick={() => {
                    setFileOutputName(fileOutputName);
                    setFileOutputNameEditOpen(false);
                  }}
                />
              </>
            ) : (
              <>
                <Text width={inputWidth}>{fileOutputName}</Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <EditIconButton
                    ariaLabel="edit name"
                    label="Edit Name"
                    onClick={() => setFileOutputNameEditOpen(true)}
                    h="6"
                  />
                )}
              </>
            )}
          </Flex>
          {/* *** Postgres Query Edit Management *** */}
          <Table borderWidth="1px">
            <Thead>
              <Tr>
                <Th>Type</Th>
                <Th> Params</Th>
                {wipConfig && <Th>Edit</Th>}
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>
                  {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
                    <Select
                      value={postgresQuery.type}
                      onChange={(val) => {
                        if (val && val !== postgresQuery.type) {
                          const query = lodash.cloneDeep(
                            getIn(wipConfig.formProps.values, queryPropertyKey),
                          ) as PostgresQueryType;
                          query.type = val;
                          const updatedQuery = updatePostgresQueryArrayResultShell(
                            { query },
                          );
                          wipConfig.formProps.setFieldValue(
                            queryPropertyKey,
                            updatedQuery,
                          );
                        }
                      }}
                      options={queryResultSetTypeOptions}
                      width="xl"
                    />
                  ) : (
                    <Text>{postgresQuery.type}</Text>
                  )}
                </Td>

                <Td>
                  <Editor
                    defaultLanguage="json"
                    value={JSON.stringify(
                      postgresQuery.parameterConfig,
                      null,
                      4,
                    )}
                    options={{ readOnly: true, minimap: { enabled: false } }}
                    theme="vs-dark"
                    height="15vh"
                  />
                </Td>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <Td>
                    <EditPostgresQueryModal
                      isOpen={isOpen}
                      onCancel={() => setIsOpen(false)}
                      onSave={onSave}
                      postgresQuery={postgresQuery}
                      wipConfig={wipConfig}
                    />
                    <EditIconButton
                      ariaLabel="edit query"
                      label="Edit Query"
                      onClick={() => setIsOpen(true)}
                    />
                  </Td>
                )}
              </Tr>
            </Tbody>
          </Table>
          {/* *** Column Names Management *** */}
          <ReportingOutputDocxColumnConfigContainer
            columnConfigs={postgresQueryOutputConfig.columns}
            wipConfig={wipConfig}
            basePropertyKey={`${fullPropertyKey}.config.columns`}
            columnNames={columnNames}
          />
        </>
      ) : null}
    </Flex>
  );
};
